import { CSSProperties, FC, useMemo, useState } from 'react'
import {
  Cell,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  Header,
  useReactTable,
} from '@tanstack/react-table'
import useTableColumns from './useTableColumns'
import cn from './index.module.css'
import { Empty, Spin } from 'antd'
import classNames from 'classnames'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { LoadingOutlined } from '@ant-design/icons'
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  IFinancialTransactionsReportData,
  IFinancialTransactionsReportItem,
} from '../utils/types'
import { TextCell } from '@pages/Admin/Reports/Payments/components/Cells/Cells'

interface IExerciseHomeworkReportTable {
  loading: boolean
  data: IFinancialTransactionsReportData
  sorting: { key: string; order: 'asc' | 'desc' | 'none' }[]
  onSortingClick: (key: string) => void
  canRemove: boolean
  onRemove: (paymentRowId: number) => void
}

const ExerciseHomeworkReportTable: FC<IExerciseHomeworkReportTable> = ({
  loading,
  data,
  sorting,
  onSortingClick,
  canRemove,
  onRemove,
}) => {
  const columns = useTableColumns(canRemove, onRemove)

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: ['index'],
  })

  const tableData: IFinancialTransactionsReportItem[] = useMemo(() => {
    return data.data ?? []
  }, [data.data])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning,
    },
    onColumnPinningChange: setColumnPinning,
  })

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      sensors={sensors}
    >
      <div className={cn.tableWrapper}>
        <table className={cn.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={classNames(cn.rowHeaderWrapper, cn.rowWrapper)}
              >
                <SortableContext
                  items={columns.map((item) => item.id ?? '-')}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header, index) => (
                    <DraggableTableHeader
                      key={header.id}
                      header={header}
                      sorting={
                        sorting.find((item) => item.key === header.id)?.order ??
                        null
                      }
                      onSorting={() => onSortingClick(header.id)}
                    />
                  ))}
                </SortableContext>
              </tr>
            ))}
          </thead>
          <tbody className={cn.tableBody}>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className={classNames(cn.rowWrapper)}>
                {row.getVisibleCells().map((cell, index) => (
                  <SortableContext
                    key={cell.id}
                    items={columns.map((item) => item.id ?? '-')}
                    strategy={horizontalListSortingStrategy}
                  >
                    <DragAlongCell key={cell.id} cell={cell} />
                  </SortableContext>
                ))}
              </tr>
            ))}
            {!loading && table.getRowModel().rows.length !== 0 && (
              <tr className={cn.totalRow}>
                <td colSpan={table.getAllColumns().length}>
                  <TextCell
                    text={`Сумма: ${data?.sum ?? 0}`}
                    className={cn.totalRowCell}
                    styles={{ width: '100%', textAlign: 'start' }}
                  />
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div className={cn.fullLineWrapper}>
                    <Spin
                      indicator={
                        <LoadingOutlined
                          spin
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                    />
                  </div>
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div className={cn.fullLineWrapper}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={'Данные на найдены'}
                    />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </DndContext>
  )
}

const DraggableTableHeader = ({
  header,
  isEditMode = false,
  sorting = undefined,
  onSorting = () => {},
  isVisible = true,
  onChangeVisibility = () => {},
}: {
  header: Header<any, unknown>
  sorting?: 'none' | 'asc' | 'desc' | null
  onSorting?: () => void
  isEditMode?: boolean
  isVisible?: boolean
  onChangeVisibility?: () => void
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: !isEditMode,
    })

  const isPinned = header.column.getIsPinned()

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? 'sticky' : 'relative',
    transform: isEditMode ? CSS.Translate.toString(transform) : '', // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    width: header.column.getSize(),
    cursor: isEditMode ? 'pointer' : 'default',
    zIndex: isPinned || isDragging ? 1 : 0,
    left:
      isPinned === 'left'
        ? `calc(${header.column.getStart('left') / 16} * var(--adapt-rem))`
        : undefined,
  }

  return (
    <th
      className={classNames(cn.headerCell, !isVisible && cn.cellHidden)}
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div style={{ display: 'flex' }}>
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </div>
    </th>
  )
}

const DragAlongCell = ({
  cell,
  isEditMode = false,
  onClick = undefined,
  isVisible = true,
}: {
  cell: Cell<any, IExerciseHomeworkReportTable>
  isEditMode?: boolean
  isVisible?: boolean
  onClick?: () => void
}) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
    disabled: !isEditMode,
  })

  const isPinned = cell.column.getIsPinned()

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? 'sticky' : 'relative',
    transform: isEditMode ? CSS.Translate.toString(transform) : '', // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    width: cell.column.getSize(),
    zIndex: isPinned || isDragging ? 1 : 0,
    left:
      isPinned === 'left'
        ? `calc(${cell.column.getStart('left') / 16} * var(--adapt-rem))`
        : undefined,
  }

  return (
    <td
      style={style}
      ref={setNodeRef}
      className={classNames(isVisible === false && cn.cellHidden)}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  )
}

export default ExerciseHomeworkReportTable
